import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";
import reportApiController from "../../services/report.service";
import HandleError from "../HandleError";
import HandleResponse from "../HandleResponse";
import { normalizeClientCartProductRows, normalizeReportListResponse } from "../../services/reportContract";
import { reportRowsFromData } from "../../services/reportRenderableRows";

export const usersAdapter = createEntityAdapter();

export const ReportListViewApi = createAsyncThunk("report/listview", async (formValues, thunkAPI) => {
  try {
    const resposedata = await reportApiController
      .view(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "listview"))
      .catch((error) => HandleError(thunkAPI, error, "listview"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const PrintoutApi = createAsyncThunk("report/Printout", async (formValues, thunkAPI) => {
  try {
    const resposedata = await reportApiController
      .printout(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "Printout"))
      .catch((error) => HandleError(thunkAPI, error, "Printout"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const clientByProductMoreProductApi = createAsyncThunk("report/moreproducts", async (formValues, thunkAPI) => {
  try {
    const resposedata = await reportApiController
      .moreclientbyproducts(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "moreproducts"))
      .catch((error) => HandleError(thunkAPI, error, "moreproducts"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

const safeReportError = (action) => {
  const payload = action.payload;
  const code = payload?.message?.error?.code || payload?.error?.code || payload?.code;
  if (code === "future_stock_as_of_date") {
    return "Stock As Of cannot be in the future. Choose today or an earlier date in your salon’s timezone, or clear the date.";
  }
  return code === "sales_measures_unavailable"
    ? "This report cannot be calculated because some financial records are incomplete. Please contact support. Previous figures have been cleared."
    : "Unable to load report data. Please try again.";
};

const initialState = {
  isListView: [],
  /** Rows accumulated for export only — keeps the on-screen table on one page while loading. */
  exportListRows: null,
  isOpenListModal: "",
  isScreenReport: "",
  isStaffFilter: "",
  isSupplierFilter: "",
  isServiceFilter: "",
  isProductFilter: "",
  isPrintFromApi: "",
  isSaleByProductSort:"",
  isClientByProductMoreProduct : [],
  isLastAbsentClientsParams: null,
  isLastStockLevelsParams: null,
  reportError: "",
  isApiStatus: {
    ReportListViewApi: "",
    PrintoutApi: "",
    clientByProductMoreProductApi:""
  },
};

const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    reset: () => initialState,
    OpenListModal: (state = initialState, action) => {
      state.isOpenListModal = action.payload;
    },
    ScreenReport: (state, action) => {
      state.isScreenReport = action.payload;
    },
    StaffFilter: (state, action) => {
      state.isStaffFilter = action.payload;
    },
    ResetStaffFilter: (state) => {
      state.isStaffFilter = "";
    },
    SupplierFilter: (state, action) => {
      state.isSupplierFilter = action.payload;
    },
    ResetSupplierFilter: (state) => {
      state.isSupplierFilter = "";
    },
    ServiceFilter: (state, action) => {
      state.isServiceFilter = action.payload;
    },
    ResetServiceFilter: (state) => {
      state.isServiceFilter = "";
    },
    ProductFilter: (state, action) => {
      state.isProductFilter = action.payload;
    },
    ResetProductFilter: (state) => {
      state.isProductFilter = "";
    },
    PrintContent: (state, action) => {
      state.isPrint = action.payload;
    },
    PrintContentFromApi: (state, action) => {
      state.isPrintFromApi = action.payload;
    },
    SaleByProductSort: (state, action) => {
      state.isSaleByProductSort = action.payload;
    },
    initExportListFromView: (state) => {
      state.exportListRows = reportRowsFromData(state.isListView?.data);
    },
    clearExportListRows: (state) => {
      state.exportListRows = null;
    },
  },
  extraReducers: (builder) => {
    builder
    .addCase(ReportListViewApi.pending, (state, action) => {
      if (!action.meta?.arg?.forExport) {
        state.activeReportRequestId = action.meta.requestId;
        state.activeExportRequestId = null;
        state.exportListRows = null;
        state.isPrint = "";
        state.isApiStatus.ReportListViewApi = "loading";
        state.reportError = "";
      } else {
        state.activeExportRequestId = action.meta.requestId;
      }
    })
    .addCase(ReportListViewApi.fulfilled, (state, action) => {
      const requestedScreen = action.meta?.arg?.ScreenReport;
      if (requestedScreen && state.isScreenReport?.uniquename && requestedScreen !== state.isScreenReport.uniquename) return;
      const forExport = action.meta?.arg?.forExport === true;
      if (!forExport && state.activeReportRequestId && state.activeReportRequestId !== action.meta.requestId) return;
      const normalizedPayload = normalizeReportListResponse(action.payload);

      if (forExport) {
        if (state.activeExportRequestId !== action.meta.requestId || state.reportError) return;
        const newRows = reportRowsFromData(normalizedPayload.data);
        state.exportListRows = [...(state.exportListRows || []), ...newRows];
        state.isApiStatus.ReportListViewApi = "succeeded";
        return;
      }

      if (action.meta?.arg?.ScreenReport === "absent_clients" && !forExport) {
        state.isLastAbsentClientsParams = action.meta.arg;
      }
      state.reportError = "";
      if (action.meta?.arg?.ScreenReport === "stock_levels" && !forExport) {
        state.exportListRows = null;
        state.isLastStockLevelsParams = action.meta.arg;
      }
      const replacePage = action.meta?.arg?.replacePage === true;
      let old_current_page = state.isListView.current_page ? state.isListView.current_page : "";
      let new_current_page = normalizedPayload.current_page ? normalizedPayload.current_page : "";
      let viewdata = state.isListView && state.isListView.data;
      let newviewdata = normalizedPayload && normalizedPayload.data;
      state.isListView = normalizedPayload;
      if (
        !replacePage &&
        old_current_page &&
        new_current_page &&
        old_current_page < new_current_page &&
        old_current_page != new_current_page
      ) {
        viewdata && newviewdata ? (state.isListView.data = [...viewdata, ...newviewdata]) : normalizedPayload;
      }
      state.isApiStatus.ReportListViewApi = 'succeeded';
    })
    .addCase(ReportListViewApi.rejected, (state, action) => {
      const requestedScreen = action.meta?.arg?.ScreenReport;
      if (requestedScreen && state.isScreenReport?.uniquename && requestedScreen !== state.isScreenReport.uniquename) return;
      if (!action.meta?.arg?.forExport && state.activeReportRequestId && state.activeReportRequestId !== action.meta.requestId) return;
      if (action.meta?.arg?.forExport && state.activeExportRequestId !== action.meta.requestId) return;
      state.isListView = [];
      state.exportListRows = null;
      state.isPrint = "";
      state.activeExportRequestId = null;
      state.reportError = safeReportError(action);
      state.isApiStatus.ReportListViewApi = 'failed';
    })
    .addCase(PrintoutApi.pending, (state) => {
      state.isApiStatus.PrintoutApi = 'loading';
    })
    .addCase(PrintoutApi.fulfilled, (state, action) => {
      state.isPrint = action.payload;
      state.isApiStatus.PrintoutApi = 'succeeded';
    })
    .addCase(PrintoutApi.rejected, (state) => {
      state.isPrint = "";
      state.isApiStatus.PrintoutApi = 'failed';
    })
    .addCase(clientByProductMoreProductApi.pending, (state) => {
      state.isApiStatus.clientByProductMoreProductApi = 'loading';
    })
    .addCase(clientByProductMoreProductApi.fulfilled, (state, action) => {
      state.isClientByProductMoreProduct = normalizeClientCartProductRows(action.payload);
      state.isApiStatus.clientByProductMoreProductApi = 'succeeded';
    })
    .addCase(clientByProductMoreProductApi.rejected, (state) => {
      state.isClientByProductMoreProduct = [];
      state.isApiStatus.clientByProductMoreProductApi = 'failed';
    });
  },
});
// Action creators are generated for each case reducer function
export const { reset, OpenListModal, ScreenReport, StaffFilter, ResetStaffFilter, SupplierFilter, ResetSupplierFilter, ServiceFilter, ResetServiceFilter, ProductFilter, ResetProductFilter, PrintContent, PrintContentFromApi, SaleByProductSort, initExportListFromView, clearExportListRows } = reportSlice.actions;
export default reportSlice.reducer;
